export default function HeroSection() {
  return (
    <section id="hero" className="relative w-full min-h-screen flex items-center overflow-hidden bg-secondary-50">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-accent-100/50 blur-3xl -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-accent-200/30 blur-3xl translate-y-1/2 -translate-x-1/3"></div>

      <div className="relative w-full px-6 md:px-10 lg:px-16 py-32 md:py-36">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Left - Main content */}
            <div className="max-w-xl">
              {/* EST badge */}
              <div className="inline-flex items-center gap-3 mb-8">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-xs font-bold tracking-widest uppercase">
                  Est. 2017
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                <span className="text-foreground-500 text-xs font-medium tracking-wide uppercase">
                  100% Ethics-Led Recruitment
                </span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground-900 leading-[1.05] mb-6">
                We don&apos;t just fill
                <br />
                roles. We{' '}
                <span className="relative inline-block text-[#f0a0b5] italic">
                  build
                </span>
                <br />
                the people behind
                <br />
                them.
              </h1>

              <p className="text-foreground-400 text-base md:text-lg leading-relaxed mb-10 max-w-md">
                A pragmatic, people-first approach to every hiring challenge. This is our common purpose — and the way we contribute to society.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/services"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-300 cursor-pointer group"
                >
                  Explore Our Services
                  <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </a>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-foreground-200 text-foreground-800 font-semibold text-sm whitespace-nowrap hover:border-accent-300 hover:text-accent-500 transition-all duration-300 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </span>
                  Let&apos;s Talk
                </a>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap items-center gap-8 md:gap-12 mt-14 pt-10 border-t border-foreground-100">
                {[
                  { value: '2017', label: 'EST.' },
                  { value: '100%', label: 'ETHICS-LED' },
                  { value: 'HR+', label: 'INTEGRATED SERVICES' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                      {stat.value}
                    </div>
                    <div className="text-foreground-500 text-xs md:text-sm font-medium mt-0.5">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Company description */}
            <div className="max-w-lg lg:pl-8">
              <div className="relative">
                {/* Decorative quote mark */}
                <div className="absolute -top-10 -left-6 text-8xl font-heading text-foreground-300/60 select-none leading-none">
                  &ldquo;
                </div>

                <div className="relative space-y-6">
                  <p className="text-foreground-600 text-lg md:text-xl leading-relaxed font-medium" style={{position:'relative'}}>
                    <span className="font-heading text-2xl font-bold text-foreground-900">JobManifest</span> is an integrated HR services company built on one belief:{' '}
                    <span className="text-foreground-800 font-semibold">the right people in the right places change everything.</span>{' '}
                    Founded by <span className="text-foreground-700 font-semibold">Late Mrs. Kamna Taneja</span>, we&apos;ve been turning hiring challenges into human-capital wins since 2017.
{/* Decorative quote mark */}
                <div className="text-8xl font-heading text-foreground-300/60 select-none leading-none">
                  &rdquo;
                </div>
                  </p>
                  <div className="w-16 h-px bg-accent-300"></div>

                  <p className="text-foreground-500 text-base leading-relaxed">
                    When people grow, organisations grow. Human capital isn&apos;t a line item — it&apos;s the whole strategy.
                  </p>

                  {/* Pill values */}
                  <div className="flex flex-col gap-3 pt-4">
                    {[
                      { icon: 'ri-shield-check-line', label: '100% Ethics-Led Practice' },
                      { icon: 'ri-user-heart-line', label: 'People-First Approach' },
                      { icon: 'ri-award-line', label: 'Integrated HR Services' },
                    ].map((pillar) => (
                      <div
                        key={pillar.label}
                        className="flex items-center gap-3 px-5 py-3 rounded-xl bg-background-50 border border-background-200 hover:border-accent-200 transition-all duration-300 cursor-default"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                          <i className={`${pillar.icon} text-accent-500 text-sm`}></i>
                        </span>
                        <span className="text-foreground-700 text-sm font-semibold">{pillar.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:block">
        <a href="#about" className="flex flex-col items-center gap-2 text-foreground-400 hover:text-accent-500 transition-colors cursor-pointer">
          <span className="text-xs tracking-widest uppercase font-medium">Discover Our Story</span>
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-down-line animate-bounce text-sm"></i>
          </span>
        </a>
      </div>
    </section>
  );
}
// ... existing code ...